import React from "react";

import { useApolloClient } from "@apollo/client";

import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_PARAMETER, { UPDATE_PARAMETER } from "../../provider/Parameters/mutation";
import { PARAMETERS } from "../../provider/Parameters/query";

/** utils */
import { getGraphsqErros } from "../../utilities/functions";

const Crud = () => {
    const settings = useSettings();
    const apollo = useApolloClient();

    const toast = React.useRef<any>(null);
    const dt = React.useRef<any>(null);

    const [records, setRecords] = React.useState<any[]>([]);
    const [record, setRecord] = React.useState<iParameter>();

    const [loading, setLoading] = React.useState<boolean>(false);
    const [recordDialog, setRecordDialog] = React.useState<boolean>(false);
    const [deleteRecordDialog, setDeleteRecordDialog] = React.useState<boolean>(false);

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [globalFilter, setGlobalFilter] = React.useState<any>(null);

    let emptyRecord: iParameter = {
        name: "",
        key: "",
        value: "",
    };

    const loadRecords = (cleanRecord: boolean = false) => {
        setLoading(true);

        apollo
            .query({
                query: PARAMETERS,
                variables: {
                    // limit: perPage,
                    // filter: filter,
                },
                fetchPolicy: "no-cache",
            })
            .then(response => {
                const data = response.data.parameters;

                setRecords(data);
                setLoading(false);
            })
            .catch(error => {
                setRecords([]);
                settings?.setErrorMessage(getGraphsqErros(error));
                settings?.setVisibleError(true);
                setLoading(false);
            });
    };

    const openNew = () => {
        setRecord(emptyRecord);
        setSubmitted(false);
        setRecordDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setRecordDialog(false);
    };

    const hideDeleteRecordDialog = () => {
        setDeleteRecordDialog(false);
    };

    const saveRecord = () => {
        setSubmitted(true);

        if (!record?.name || !record.key || !record.value) {
            return;
        }

        const _record: any = { ...record };

        delete _record.__typename;

        setLoading(true);
        let mutation = CREATE_PARAMETER;

        if (record?.id) {
            _record.id = parseInt(`${_record.id}`);
            mutation = UPDATE_PARAMETER;

            delete _record.key;
        }

        apollo
            .mutate({
                mutation,
                variables: {
                    data: {
                        ..._record,
                    },
                },
            })
            .then(response => {
                setLoading(false);

                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

                loadRecords();
                setRecordDialog(false);
                setRecord(emptyRecord);
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    const editRecord = (record: iParameter) => {
        const _record: iParameter = { ...record };

        setRecord({ ..._record });
        setRecordDialog(true);
    };

    const confirmDeleteRecord = (record: any) => {
        setRecord(record);
        setDeleteRecordDialog(true);
    };

    const deleteRecord = () => {
        apollo
            .mutate({
                mutation: UPDATE_PARAMETER,
                variables: {
                    data: {
                        id: parseInt(`${record?.id}`),
                        is_deleted: true,
                    },
                },
            })
            .then(response => {
                let _records = records.filter((val: any) => val.id !== record?.id);
                setRecords(_records);
                setDeleteRecordDialog(false);
                setRecord(emptyRecord);
                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    const onInputChange = (e: any, name: string) => {
        let val = (e.target && e.target.value) || "";

        let _record: any = { ...record };
        _record[name] = val;

        setRecord(_record);
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
                {/* <Button
                    label="Delete"
                    icon="pi pi-trash"
                    className="p-button-danger mb-2"
                    onClick={confirmDeleteSelected}
                    disabled={!selectedRecords || !selectedRecords.length}
                /> */}
            </React.Fragment>
        );
    };

    const codeBodyTemplate = (rowData: iParameter) => {
        return (
            <>
                <span className="p-column-title">Chave.</span>
                {rowData.key}
            </>
        );
    };

    const nameBodyTemplate = (rowData: iParameter) => {
        return (
            <>
                <span className="p-column-title">Nome</span>
                {rowData.name}
            </>
        );
    };

    const valueBodyTemplate = (rowData: iParameter) => {
        return (
            <>
                <span className="p-column-title">Valor</span>
                {rowData.value}
            </>
        );
    };

    const actionBodyTemplate = (rowData: iParameter) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0" onClick={() => confirmDeleteRecord(rowData)} />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{loading ? "Carregando parametros..." : "Parametros"}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
            </span>
        </div>
    );

    const recordDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
            <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
        </>
    );

    const deleteRecordDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
            <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
        </>
    );

    React.useEffect(() => {
        loadRecords(true);
    }, []);

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

                    <DataTable
                        ref={dt}
                        value={records}
                        dataKey="id"
                        paginator
                        stripedRows={true}
                        rowHover
                        showGridlines
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                        globalFilter={globalFilter}
                        emptyMessage="Nenhum registro encontrado"
                        header={header}
                        responsiveLayout="scroll">
                        <Column field="id" header="Chave" sortable body={codeBodyTemplate} headerStyle={{}}></Column>
                        <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}}></Column>
                        <Column field="key" header="Valor" body={valueBodyTemplate} sortable headerStyle={{}}></Column>

                        <Column header="Ações" className="flex flex-row align-items-start justify-content-start" body={actionBodyTemplate}></Column>
                    </DataTable>

                    {/* Modal adicionar editar */}
                    <Dialog
                        visible={recordDialog}
                        style={{ width: "550px" }}
                        header={record?.id ? "Detalhes do Parâmetro" : "Novo Parâmetro"}
                        modal
                        className="p-fluid"
                        footer={recordDialogFooter}
                        onHide={hideDialog}>
                        <div className="flex flex-column md:flex-row flex-wrap">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="key">* Chave</label>
                                <InputText
                                    id="key"
                                    value={record?.key}
                                    onChange={e => onInputChange(e, "key")}
                                    disabled={record?.id ? true : false}
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.key })}
                                />
                                {submitted && !record?.key && <small className="p-invalid">CHAVE é obrigatório.</small>}
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="name">* Nome</label>
                                <InputText
                                    id="name"
                                    value={record?.name}
                                    onChange={e => onInputChange(e, "name")}
                                    required
                                    autoFocus
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                                />
                                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
                            </div>
                        </div>
                        <div className="flex flex-column md:flex-row flex-wrap ">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="value">* Valor</label>
                                <InputText
                                    id="value"
                                    type={"text"}
                                    value={record?.value}
                                    autoComplete={"new-password"}
                                    onChange={e => onInputChange(e, "value")}
                                    required
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.value })}
                                />
                                {submitted && record?.value === "" && <small className="p-invalid">Um valor é obrigatório.</small>}
                            </div>
                        </div>
                    </Dialog>

                    {/* Modal remover registro unico */}
                    <Dialog
                        visible={deleteRecordDialog}
                        // style={{ width: "450px" }}
                        header="Atenção"
                        modal
                        footer={deleteRecordDialogFooter}
                        onHide={hideDeleteRecordDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                            {record && (
                                <span>
                                    Tem certeza de que deseja excluir <b>{record?.name}</b>?
                                </span>
                            )}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Crud;
